/**
 * 题目工具：分页与题号计算。
 * - pagebreak 题目按"分割符"语义把 questions 列表切成多页
 * - pageNumberMap 给每个题号计算所在页（1 开始；pagebreak 自身也带一个页号，
 *   即"它前面的内容所在的页"——与编辑端的视觉一致）
 * - displayIndexMap 给出"用户视角的题号"——statement / pagebreak 不占序号
 */
import type { QuestionType } from './types';
import { isNonAnswerType } from './types';

export function paginateQuestions<T extends { id: string; type: QuestionType }>(questions: T[]): T[][] {
  const pages: T[][] = [];
  let current: T[] = [];
  for (const q of questions) {
    if (q.type === 'pagebreak') {
      pages.push(current);
      current = [];
    } else {
      current.push(q);
    }
  }
  pages.push(current);
  // 去掉末尾空页（最后一个 pagebreak 后没题的情况）
  return pages.filter((p, i) => i < pages.length - 1 || p.length > 0);
}

/**
 * 给每个题目（含 pagebreak）映射所在页号（1 开始）。
 * 规则：pagebreak 自己归属"它前面所有题所在的页"。
 * 例：[Q1, Q2, pagebreak, Q3, pagebreak, Q4] →
 *   {Q1:1, Q2:1, pagebreak#1:1, Q3:2, pagebreak#2:2, Q4:3}
 */
export function pageNumberMap<T extends { id: string; type: QuestionType }>(questions: T[]): Map<string, number> {
  const map = new Map<string, number>();
  let pageNum = 1;
  for (const q of questions) {
    map.set(q.id, pageNum);
    if (q.type === 'pagebreak') pageNum += 1;
  }
  return map;
}

/**
 * 用户视角题号（statement / pagebreak 不占序号）。
 * statement 显示 ★ 不需要数字；pagebreak 不显示题号；可作答题型从 1 累计。
 */
export function displayIndexMap<T extends { id: string; type: QuestionType }>(questions: T[]): Map<string, number> {
  const map = new Map<string, number>();
  let n = 0;
  for (const q of questions) {
    if (!isNonAnswerType(q.type)) n += 1;
    map.set(q.id, n);
  }
  return map;
}

/** 深拷贝一道题并换成新 id（复制题目 / 复制本页；题图、选项图等一并复制） */
export function cloneQuestion<T extends { id: string }>(q: T, newId: string): T {
  const copy = JSON.parse(JSON.stringify(q)) as T;
  copy.id = newId;
  return copy;
}

/**
 * 某个分页栏"所属页"的题目区间 [start, end)：上一分页栏之后 到 本分页栏之前。
 * 例：[Q1, Q2, PB1, Q3, PB2] → pageRangeBefore(list, 4) = [3, 4]（即 Q3）
 */
export function pageRangeBefore<T extends { id: string; type: QuestionType }>(questions: T[], pagebreakIndex: number): [number, number] {
  let start = 0;
  for (let i = pagebreakIndex - 1; i >= 0; i -= 1) {
    if (questions[i].type === 'pagebreak') {
      start = i + 1;
      break;
    }
  }
  return [start, pagebreakIndex];
}
